import { george, NetbankingAPI, pr, theme } from "aaa";
import { ui } from "components/util/uiHelpers";
import { chunk } from "lodash";
import { observer } from "mobx-react";
import { themeColors } from "muiTheme";
import { NetbankingAPIClient } from "network/APIClient";
import * as React from "react";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import { history } from "stores/RouterStore";
import { getDefaultKeyPressHandler } from "util/helpers";
import { CardTanSettingsRoutes } from "./CardTanSettingsRoutes";
import { CardTanSettingsStore } from "./CardTanSettingsStore";

const LazyAvatarImage = observer(({ accountId }: { accountId: string }) => {
    const [blob, setBlob] = React.useState<Blob | undefined>(undefined);

    React.useEffect(() => {
        const loadImage = async () => {
            try {
                const target = NetbankingAPI.getAccountImage(accountId);
                const imageBlob = await NetbankingAPIClient.requestBlob(target);

                if (imageBlob) {
                    setBlob(imageBlob);
                }
            } catch (error) {
                // SILENT FAIL
            }
        };

        loadImage();
    }, []);

    return (
        <div
            style={{
                display: "block",
                width: 40,
                height: 40,
                borderRadius: 40,
                backgroundColor: ui.isDarkMode ? themeColors.d_grey_200 : themeColors.grey_200,
                backgroundImage: `URL(${blob ? URL.createObjectURL(blob) : ""})`,
                backgroundSize: "cover"
            }}
        />
    );
});

export const CardTanSettingsCards = observer(({ store }: { store: CardTanSettingsStore }) => {
    const [didAutofocus, setDidAutofocus] = React.useState(false);

    const handleSelectDebitCard = (account: string, sequenceNumber: number) => {
        store.updatedDebitCards = store.updatedDebitCards.map(debitCard =>
            debitCard.account === account && debitCard.sequenceNumber === sequenceNumber
                ? {
                    ...debitCard,
                    flags: debitCard.flags?.includes("cardTanEnabled")
                        ? debitCard.flags.filter(flag => flag !== "cardTanEnabled")
                        : ["cardTanEnabled"]
                }
                : debitCard);
    };

    const handleSubmitCards = async () => {
        try {
            await store.updateDebitCards();
            pushRoute(CardTanSettingsRoutes.CARDS_SIGNING);
        } catch (error) {
            pushRoute("/error");
        }
    };

    const handleBack = async () => {
        // reset changes
        store.updatedDebitCards = store.debitCards;
        pushRoute(CardTanSettingsRoutes.ROOT);
    };

    const ref = React.useRef<HTMLAnchorElement | null>(null);

    React.useEffect(() => {
        if (ref.current && !didAutofocus) {
            ref.current.focus();
            setDidAutofocus(true);
        }

    }, []);

    const accountsLoaded = !!store.accounts;

    React.useEffect(() => {
        const loadAccounts = async () => {
            await store.loadAccounts();
        };

        if (!accountsLoaded) {
            loadAccounts();
        }
    }, [accountsLoaded]);

    if (store.isLoading || !accountsLoaded) {
        return <george.LoadingIndicator isLoading height={ui.windowHeight} />;
    }

    return (
        <george.Background>
            <george.PageWithActionButton>
                <div style={{ padding: theme.MARGIN }}>
                    <a
                        ref={ref}
                        style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", cursor: "pointer" }}
                        role="button"
                        tabIndex={0}
                        onClick={handleBack}
                        onKeyPress={getDefaultKeyPressHandler(handleBack)}
                        aria-label={ui.__("a11y.back")}
                    >
                        <pr.IconChevronLeft style={{ color: "#135EE2" }} />
                        {!externalInterfaceStore.isGeorgeApp && (
                            <pr.P_16_CONTENT style={{ color: "#135EE2", fontWeight: 600 }}>
                                {ui.__("common.back")}
                            </pr.P_16_CONTENT>
                        )}
                    </a>
                    <pr.P_24_TITLE style={{ fontWeight: 700, marginTop: theme.MARGIN }}>
                        {ui.__("cardTanSettings.setting.debitCards")}
                    </pr.P_24_TITLE>
                    <pr.P_16_CONTENT style={{ color: ui.isDarkMode ? "white" : "#0A285C", lineHeight: "22px", marginTop: 16 }}>
                        {ui.__("cardTanSettings.debitCards.description")}
                    </pr.P_16_CONTENT>
                    <div className="theme-paper" style={{ borderRadius: 8, border: ui.isDarkMode ? undefined : `1px solid ${themeColors.grey_200}`, marginTop: 16 }}>
                        {store.updatedDebitCards.map((debitCard, index) => {
                            const detailsAccount = store.accounts?.find(account => account.accountno?.iban === debitCard.account);

                            if (!detailsAccount) {
                                return null;
                            }

                            const iban = detailsAccount?.accountno && "iban" in detailsAccount?.accountno ? detailsAccount.accountno?.iban : undefined;
                            const accountNumber = detailsAccount?.accountno && "number" in detailsAccount?.accountno ? detailsAccount.accountno?.number : undefined;
                            const alias = "alias" in detailsAccount ? detailsAccount?.alias : undefined;
                            const description = detailsAccount?.description;

                            return (
                                <div
                                    key={`${debitCard.account}_${debitCard.sequenceNumber}`}
                                    onClick={() => handleSelectDebitCard(debitCard.account, debitCard.sequenceNumber)}
                                    style={{
                                        position: "relative",
                                        display: "flex",
                                        alignItems: "center",
                                        height: 72,
                                        // padding: "16px 16px 16px 12px",
                                        boxSizing: "border-box",
                                        // borderLeft: `4px solid ${detailsAccount?.additionalData?.accountColor?.hexCodeLight ?? "transparent"}`,
                                        cursor: "pointer",
                                        // backgroundColor: "white",
                                        borderTopLeftRadius: index === 0 ? 8 : undefined,
                                        borderTopRightRadius: index === 0 ? 8 : undefined,
                                        borderBottomLeftRadius: index === store.updatedDebitCards.length - 1 ? 8 : undefined,
                                        borderBottomRightRadius: index === store.updatedDebitCards.length - 1 ? 8 : undefined,
                                        overflow: "hidden"
                                    }}
                                >
                                    <div
                                        style={{
                                            position: "absolute",
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: 4,
                                            borderTopLeftRadius: index === 0 ? 8 : undefined,
                                            borderBottomLeftRadius: index === store.updatedDebitCards.length - 1 ? 8 : undefined,
                                            backgroundColor: (
                                                ui.isDarkMode
                                                    ? detailsAccount?.additionalData?.accountColor?.hexCodeDark
                                                    : detailsAccount?.additionalData?.accountColor?.hexCodeLight
                                            ) ?? "transparent"
                                        }}
                                    />
                                    <div style={{ padding: "16px 12px 16px 16px" }}>
                                        <LazyAvatarImage accountId={detailsAccount.id} />
                                    </div>
                                    <div
                                        style={{
                                            boxSizing: "border-box",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            flexGrow: 1,
                                            marginLeft: 12,
                                            height: "100%",
                                            paddingRight: 16,
                                            borderBottom: index < store.updatedDebitCards.length - 1
                                                ? `1px solid ${ui.isDarkMode ? themeColors.d_grey_100 : themeColors.grey_200}`
                                                : undefined,
                                        }}
                                    >
                                        <div>
                                            <pr.P_12_CONTENT style={{ lineHeight: "16px" }}>
                                                {debitCard.physicalCardNumber}
                                            </pr.P_12_CONTENT>
                                            <pr.P_16_TITLE style={{ color: ui.isDarkMode ? "white" : "#0A285C", lineHeight: "22px" }}>
                                                <b>{alias ?? description}</b>
                                            </pr.P_16_TITLE>
                                            <pr.P_12_CONTENT style={{ lineHeight: "16px" }}>
                                                {iban ? chunk(iban, 4).map((ibanPart) => ibanPart.join("")).join(" ") : accountNumber}
                                            </pr.P_12_CONTENT>
                                        </div>
                                        <div>
                                            {debitCard.flags?.includes("cardTanEnabled")
                                                ? <pr.IconCheckbox style={{ display: "block", color: themeColors.blue_300 }} />
                                                : <pr.IconCheckboxOutline style={{ display: "block", color: themeColors.grey_300 }} />
                                            }
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <george.ActionButton
                    onClick={handleSubmitCards}
                    primary
                    messageId="common.save"
                />
            </george.PageWithActionButton>
        </george.Background>
    );
});


// WEBPACK FOOTER //
// ./src/components/loginManagement/cardTanSettings/CardTanSettingsCards.tsx